import React, { createContext, useContext, useEffect, useState } from 'react';
import { menuService } from '../services/menu.service';

type PermissionContextType = {
  pageId: number;
  setPageId: (pageId: number) => void;
  permissions: Permission[];
  hasPermission: (permissionId: number) => boolean;
};

type Permission = {
  permissionId: number;
  permissionName: string;
  description: string;
};

export const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pageId, setPageId] = useState<number>(0);
  const [permissions, setPermissions] = useState<Permission[]>([]);

  const hasPermission = (permissionId: number) => {
    return permissions.some(permission => permission.permissionId === permissionId);
  };

  useEffect(() => {
    if (pageId === 0) {
      return;
    }

    const fetchPermissions = async () => {
      try {
        const response = await menuService.getPermissions(pageId);
        setPermissions(response.data.data ?? []);
      } catch (error) {
        console.error('Error fetching permissions:', error);
      }
    };

    fetchPermissions();
  }, [pageId]);

  return (
    <PermissionContext.Provider value={{ permissions, hasPermission, setPageId, pageId }}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};
